/**
 * Canonical list of outsourcing service categories (terceirizados).
 * Single source of truth — all apps import from here.
 *
 * Mirrors `serviceCategoryEnum` in `packages/db/src/schema/enums.ts`; the two
 * must stay in sync (a new category needs a migration on the PG enum before
 * it can be added here).
 *
 * `SERVICE_CATEGORY_KEYS` is declared as a literal tuple on purpose: `z.enum`
 * infers the union from it, so a value validated by the API stays assignable
 * to the Drizzle enum column in `eq(providers.category, value)`. Deriving the
 * tuple with `.map()` would widen it to `string[]` and break those queries.
 */
export const SERVICE_CATEGORY_KEYS = [
  'engine_rebuild',
  '3d_printing',
  'bodywork',
  'advanced_electrical',
  'adaptation',
  'other',
] as const;

export type ServiceCategoryKey = (typeof SERVICE_CATEGORY_KEYS)[number];

/** Rótulos PT-BR. O `Record` obriga a cobrir toda chave nova em tempo de compilação. */
const SERVICE_CATEGORY_LABELS: Record<ServiceCategoryKey, string> = {
  engine_rebuild: 'Retífica Motor',
  '3d_printing': 'Impressão 3D',
  bodywork: 'Funilaria',
  advanced_electrical: 'Elétrica Avançada',
  adaptation: 'Adaptação',
  other: 'Outro',
};

export const SERVICE_CATEGORIES: readonly {
  key: ServiceCategoryKey;
  label: string;
}[] = SERVICE_CATEGORY_KEYS.map((key) => ({
  key,
  label: SERVICE_CATEGORY_LABELS[key],
}));

export function getServiceCategoryLabel(key: string): string {
  return SERVICE_CATEGORY_LABELS[key as ServiceCategoryKey] ?? key;
}

export function isValidServiceCategoryKey(key: string): key is ServiceCategoryKey {
  return (SERVICE_CATEGORY_KEYS as readonly string[]).includes(key);
}
